
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import SidebarContent from '@/components/SidebarContent.vue';
import useInfoBoxState from '@/state/infoBox';
import {
  computed, defineComponent, ref, toRef, watch
} from 'vue';
import useUser from '@/mixins/useUser';
import { sample } from 'lodash';

export default defineComponent({
  name: 'SidebarContainer',
  components: {
    SidebarContent,
    BCXMarkdown,
  },
  setup(_, { slots, emit }) {
    const {
      infoBoxState, resetInfoBox, hasCustomSidebar,
    } = useInfoBoxState();
    const { tUserFallback: t } = useUser('self');

    const hasSlotContent = computed(() => !!slots?.default);

    const isOpened = toRef(infoBoxState, 'isOpened');

    const showCloseIcon = computed(() => hasSlotContent.value && hasCustomSidebar.value);

    const anythingInside = computed(() => infoBoxState.isActive || hasCustomSidebar.value);
    const currentTipIndex = ref(0);
    const resolvedTexts = computed(() => {
      if (!infoBoxState.text) return [];
      const textOrTexts = t(infoBoxState.text);
      if (!textOrTexts) return [];
      return Array.isArray(textOrTexts) ? textOrTexts : [textOrTexts];
    });
    const showTipNavigation = computed(() => !!infoBoxState.enableTipNavigation && resolvedTexts.value.length > 1);
    const canGoPrev = computed(() => showTipNavigation.value);
    const canGoNext = computed(() => showTipNavigation.value);

    const text = computed(() => {
      if (!resolvedTexts.value.length) return '';
      if (showTipNavigation.value) {
        return resolvedTexts.value[currentTipIndex.value] as string;
      }
      return sample(resolvedTexts.value) as string;
    });

    const goToPreviousTip = () => {
      if (!resolvedTexts.value.length) return;
      currentTipIndex.value = currentTipIndex.value === 0
        ? resolvedTexts.value.length - 1
        : currentTipIndex.value - 1;
    };

    const goToNextTip = () => {
      if (!resolvedTexts.value.length) return;
      currentTipIndex.value = currentTipIndex.value === resolvedTexts.value.length - 1
        ? 0
        : currentTipIndex.value + 1;
    };

    watch(anythingInside, (anything) => {
      emit('anything-inside', anything);
    }, {
      immediate: true
    });

    watch([() => infoBoxState.text, resolvedTexts], () => {
      if (!resolvedTexts.value.length) {
        currentTipIndex.value = 0;
        return;
      }
      if (showTipNavigation.value) {
        currentTipIndex.value = Math.floor(Math.random() * resolvedTexts.value.length);
        return;
      }
      if (currentTipIndex.value >= resolvedTexts.value.length) {
        currentTipIndex.value = 0;
      }
    });

    return {
      canGoNext,
      canGoPrev,
      goToNextTip,
      goToPreviousTip,
      showCloseIcon,
      showTipNavigation,
      isOpened,
      hasSlotContent,
      resetInfoBox,
      infoBoxState,
      text,
      t
    };
  }
});
